import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, ChevronDown, Loader2, LogOut, Check, Search } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/errors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

/**
 * Platform Admin-only dropdown in the header that lets the operator
 * "switch into" a customer's tenant context. Calls
 * POST /api/tenants/{id}/impersonate, swaps the local JWTs, reloads the app.
 *
 * Only renders for users with platform_role === "platform_admin" or legacy
 * role === "admin".
 */
export default function TenantSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const canSwitch = user?.platform_role === "platform_admin" || user?.role === "admin";
  const impersonating = !!localStorage.getItem("impersonating_tenant_name");
  const activeName = localStorage.getItem("impersonating_tenant_name") || "Platform Context";

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open || !canSwitch || tenants.length) return;
    setLoading(true);
    axios
      .get(`${API}/tenants`, { headers: getHeaders() })
      .then((res) => {
        // Only show tenants that can actually be switched into.
        // Deactivated / suspended tenants are managed from
        // Platform Admin → Tenants, not from the operational switcher.
        const list = Array.isArray(res.data) ? res.data : [];
        setTenants(list.filter((t) => t.status === "active" || t.status === "provisioning"));
      })
      .catch((err) => toast.error(formatApiError(err, "Failed to load tenants")))
      .finally(() => setLoading(false));
  }, [open, canSwitch, tenants.length]);

  if (!canSwitch) return null;

  const switchTo = async (tenant) => {
    setSwitching(true);
    try {
      const { data } = await axios.post(
        `${API}/tenants/${tenant.id}/impersonate`,
        {},
        { headers: getHeaders() },
      );
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      localStorage.setItem("impersonating_tenant_id", tenant.id);
      localStorage.setItem("impersonating_tenant_name", tenant.name);
      toast.success(`Switched into ${tenant.name}`);
      // Hard reload so React/route caches pick up the new tenant context cleanly.
      window.location.assign("/app");
    } catch (err) {
      toast.error(formatApiError(err, "Switch failed"));
      setSwitching(false);
    }
  };

  const exit = async () => {
    setSwitching(true);
    try {
      const { data } = await axios.post(
        `${API}/tenants/exit-impersonation`,
        {},
        { headers: getHeaders() },
      );
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      localStorage.removeItem("impersonating_tenant_id");
      localStorage.removeItem("impersonating_tenant_name");
      toast.success("Returned to Platform Context");
      window.location.assign("/app");
    } catch (err) {
      toast.error(formatApiError(err, "Exit failed"));
      setSwitching(false);
    }
  };

  const filtered = (tenants || []).filter((t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (t.name || "").toLowerCase().includes(q) ||
      (t.slug || "").toLowerCase().includes(q) ||
      (t.type || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs transition-colors ${
          impersonating
            ? "border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
            : "border-slate-600/50 bg-slate-800/40 text-slate-200 hover:bg-slate-800/70"
        }`}
        data-testid="tenant-switcher-btn"
        title="Switch tenant context"
      >
        <Building2 className="w-3.5 h-3.5" />
        <span className="max-w-[140px] truncate font-medium">{activeName}</span>
        {impersonating && (
          <span className="text-[9px] uppercase tracking-wider bg-amber-500/30 text-amber-100 px-1 rounded">
            Impersonating
          </span>
        )}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-hidden flex flex-col rounded-lg border border-slate-700/60 bg-slate-900/95 backdrop-blur-md shadow-2xl z-50"
          data-testid="tenant-switcher-menu"
        >
          <div className="p-3 border-b border-slate-700/40">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tenants..."
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-slate-800/50 border border-slate-700/40 rounded text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                data-testid="tenant-switcher-search"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-500 italic text-center py-6">
                {tenants.length === 0 ? "No tenants found" : "No matches"}
              </p>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={switching}
                  onClick={() => switchTo(t)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-800/60 border-b border-slate-700/20 last:border-b-0 transition-colors disabled:opacity-40"
                  data-testid={`tenant-switch-${t.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{t.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {t.type || "—"} · {t.slug} · {t.status}
                      </p>
                    </div>
                    {localStorage.getItem("impersonating_tenant_id") === t.id && (
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {impersonating && (
            <div className="border-t border-slate-700/40 p-2">
              <button
                type="button"
                onClick={exit}
                disabled={switching}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs bg-slate-800/60 text-slate-200 hover:bg-slate-800 border border-slate-700/40 disabled:opacity-40"
                data-testid="tenant-exit-impersonation-btn"
              >
                {switching ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
                Exit to Platform Context
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
